// Minimal typing of the cornerstone-tools 3.0.0-b.704 beta API this app builds tools on.
// The beta ships as a UMD global (public/lib/cornerstoneTools.min.js); only what we touch is typed.
import type { CornerstoneCore, CornerstoneToolsApi, CornerstoneToolsLib } from '../globals';

export interface Point {
  x: number;
  y: number;
}

export interface BoundingBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A draggable point of a measurement, in image pixel coordinates. */
export interface Handle extends Point {
  active?: boolean;
  highlight?: boolean;
  /** Not drawn as a circle by drawHandles (text boxes, not-yet-placed points). */
  drawnIndependently?: boolean;
  /** Hit-tested by its bounding box instead of by distance. */
  hasBoundingBox?: boolean;
  hasMoved?: boolean;
  movesIndependently?: boolean;
  allowedOutsideImage?: boolean;
  boundingBox?: BoundingBox;
}

/** One value line of a measurement, as the "Área de trabajo" cards show it (e.g. "Longitud:" / "12.30 mm"). */
export interface Readout {
  label: string;
  value: string;
}

/** A tool-state entry (one measurement on one image). */
export interface MeasurementData {
  visible: boolean;
  active: boolean;
  color?: string;
  invalidated?: boolean;
  complete?: boolean;
  text?: string;
  handles: Record<string, Handle>;
  /** Filled by our tools on every render; read by the annotations workspace. */
  readout?: Readout[];
  [extra: string]: unknown;
}

export interface ToolImage {
  imageId: string;
  rows: number;
  columns: number;
  width: number;
  height: number;
  color: boolean;
  slope: number;
  intercept: number;
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
}

export interface MouseDetail {
  element: HTMLElement;
  image: ToolImage;
  buttons?: number;
  currentPoints: { image: Point; canvas: Point };
}

export interface RenderDetail {
  element: HTMLElement;
  image: ToolImage;
  canvasContext: CanvasRenderingContext2D;
}

export type InteractionType = 'mouse' | 'touch';

/** Instance surface of a beta annotation tool (BaseAnnotationTool and the built-ins deriving from it). */
export interface AnnotationTool {
  name: string;
  mode: string;
  element?: HTMLElement;
  configuration: Record<string, unknown>;
  options: Record<string, unknown>;
  pointNearTool(element: HTMLElement, data: MeasurementData, coords: Point): boolean;
  renderToolData(evt: CustomEvent<RenderDetail>): void;
}

export type AnnotationToolClass = new (name?: string) => AnnotationTool;

export interface BaseToolArgs {
  name: string;
  supportedInteractionTypes: InteractionType[];
  configuration?: Record<string, unknown>;
}

export type BaseAnnotationToolClass = new (args: BaseToolArgs) => AnnotationTool;

type HandleMover = (
  detail: MouseDetail,
  toolName: string,
  data: MeasurementData,
  handle: Handle,
  done: () => void,
  preventHandleOutsideImage?: boolean,
) => void;

interface ToolStateManager {
  saveToolState(): Record<string, Record<string, { data: MeasurementData[] } | undefined> | undefined>;
}

export interface ToolStoreState {
  tools: Array<{ name: string; element?: HTMLElement; mode: string }>;
  enabledElements: HTMLElement[];
  isToolLocked: boolean;
  /** Canvas px within which a press grabs a handle (set per input by tools/proximity.ts). */
  clickProximity: number;
}

/** The beta's store, as returned by init(). */
export interface ToolStore {
  state: ToolStoreState;
  mutations: { SET_IS_TOOL_LOCKED(locked: boolean): void };
}

export interface CornerstoneToolsBeta extends CornerstoneToolsLib {
  LengthTool: AnnotationToolClass;
  EVENTS: Record<'MEASUREMENT_ADDED' | 'MEASUREMENT_MODIFIED' | 'MEASUREMENT_REMOVED', string>;
  moveNewHandle: HandleMover;
  moveNewHandleTouch: HandleMover;
  drawHandles(context: CanvasRenderingContext2D, renderData: { element: HTMLElement }, handles: Record<string, Handle>, color: string, options?: Record<string, unknown>): void;
  removeToolState(element: HTMLElement, toolName: string, data: MeasurementData): void;
  getRGBPixels(element: HTMLElement, x: number, y: number, width: number, height: number): number[];
  /** SUV of a stored pixel value on PT (undefined when the PET metadata is missing). */
  calculateSUV?(image: ToolImage, storedPixelValue: number): number | undefined;
  globalImageIdSpecificToolStateManager: ToolStateManager;
  textStyle: { getFont(): string; getFontSize(): number };
  toolStyle: { getToolWidth(): number; getActiveWidth(): number };
  toolColors: CornerstoneToolsLib['toolColors'] & { getColorIfActive(data: MeasurementData): string };
}

export interface CornerstoneToolsApiBeta extends CornerstoneToolsApi {
  store: ToolStore;
  addToolForElement(element: HTMLElement, toolClass: unknown): void;
  addEnabledElement(element: HTMLElement): void;
  getToolForElement(element: HTMLElement, name: string): AnnotationTool | undefined;
  setToolPassiveForElement(element: HTMLElement, name: string, options?: Record<string, unknown>): void;
  setToolActiveForElement(element: HTMLElement, name: string, options: Record<string, unknown>): void;
  setToolEnabledForElement(element: HTMLElement, name: string, options?: Record<string, unknown>): void;
  setToolDisabledForElement(element: HTMLElement, name: string, options?: Record<string, unknown>): void;
}

export interface CornerstoneCoreExtras extends CornerstoneCore {
  pixelToCanvas(element: HTMLElement, point: Point): Point;
  getStoredPixels(element: HTMLElement, x: number, y: number, width: number, height: number): number[];
  registerImageLoader(scheme: string, loader: (imageId: string, options?: unknown) => { promise: Promise<unknown>; cancelFn?: () => void }): void;
}

export function csTools(): CornerstoneToolsBeta {
  return window.cornerstoneTools as CornerstoneToolsBeta;
}

export function csCore(): CornerstoneCoreExtras {
  return window.cornerstone as CornerstoneCoreExtras;
}

/** A built-in beta tool class by its export name (e.g. 'LengthTool'). */
export function builtInTool(className: string): AnnotationToolClass {
  const cls = window.cornerstoneTools[className];
  if (typeof cls !== 'function') throw new Error(`cornerstone-tools has no ${className}`);
  return cls as AnnotationToolClass;
}

/**
 * The beta does not export BaseAnnotationTool, so it is taken from LengthTool's prototype chain
 * (LengthTool extends BaseAnnotationTool directly).
 */
export function baseAnnotationTool(): BaseAnnotationToolClass {
  return Object.getPrototypeOf(csTools().LengthTool) as BaseAnnotationToolClass;
}
